import styled from "@emotion/styled";
import { Search } from "@mui/icons-material";
import { Avatar, InputAdornment } from "@mui/material";
import { Checkbox, Loading, TextField } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useEmployees } from "features/hr";
import {
  FILTER_OPERATIONS,
  MEMBER_STATES,
  useMembers,
  usePartners
} from "features/members";
import { useTeams } from "features/teams";
import Fuse from "fuse.js";
import { isEmpty, map } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsInfoCircle, BsX } from "react-icons/bs";
import { useMessagesStore } from "../store";

const Container = styled.div`
  height: 100%;
  max-height: 913px;
  width: 420px;
  max-width: 100%;
  min-width: 0;
  background: white;
  padding: 22px 44px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 600px) {
    width: 100%;
    height: auto;
    max-height: none;
    padding: 16px;
  }
`;

const Tabs = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 32px;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: thin;
  -ms-overflow-style: none;
  padding-bottom: 8px;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.li`
  padding: 10px;
  background: ${({ active, theme }) => active && theme.palette.primary.main};
  color: ${({ active }) => (active ? "white" : "#ABABAB")};
  border-radius: 5px;
  margin-bottom: 9px;
  cursor: pointer;
`;

const Alert = styled.div`
  padding: 11px 19px;
  background-color: ${({ bg }) => bg};
  border: 1.5px solid ${({ borderColor }) => borderColor};
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 10px;
  max-width: 750px;
  font-size: 10px;
  line-height: 15px;

  .startIcon {
    min-height: 24px;
    min-width: 24px;
    background-color: ${({ iconBg }) => iconBg};
    display: grid;
    place-items: center;
    font-size: 14px;
    border-radius: 100%;
    color: #ffffff;
  }

  .endIcon {
    min-height: 24px;
    min-width: 24px;
    background-color: #ffffff;
    display: grid;
    place-items: center;
    border-radius: 100%;
    margin-left: auto;
    cursor: pointer;
  }
`;

const Contacts = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 16px;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  flex-grow: 1;
`;

const Contact = styled.li`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ContactName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const tabs = [
  { name: "message.newMessage.Members", value: "members" },
  { name: "message.newMessage.Teams", value: "teams" },
  { name: "message.newMessage.Partners", value: "partners" },
  { name: "message.newMessage.Employees", value: "employees" }
];

const translationMapping = {
  members: "message.newMessage.SearchMembers",
  teams: "message.newMessage.SearchTeams",
  partners: "message.newMessage.SearchPartners",
  employees: "message.newMessage.SearchEmployees"
};

export const MessageControls = () => {
  const [activeTab, setActiveTab] = useState("members");
  const recipients = useMessagesStore(state => state.recipients);
  const addRecipient = useMessagesStore(state => state.addRecipient);
  const removeRecipient = useMessagesStore(state => state.removeRecipient);
  const addMember = useMessagesStore(state => state.addMember);
  const removeMember = useMessagesStore(state => state.removeMember);
  const addTeam = useMessagesStore(state => state.addTeam);
  const removeTeam = useMessagesStore(state => state.removeTeam);
  const addPartner = useMessagesStore(state => state.addPartner);
  const removePartner = useMessagesStore(state => state.removePartner);
  const addEmployee = useMessagesStore(state => state.addEmployee);
  const removeEmployee = useMessagesStore(state => state.removeEmployee);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();

  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const membersQuery = useMembers({
    churchId: selectedChurch?.id,
    queries: {
      filter: {
        status: [
          {
            operator: FILTER_OPERATIONS.EQUAL,
            value: MEMBER_STATES.ACTIVE
          }
        ]
      }
    }
  });

  const teamsQuery = useTeams({
    churchId: selectedChurch?.id
  });

  const partnersQuery = usePartners({
    churchId: selectedChurch?.id
  });

  const employeesQuery = useEmployees({
    churchId: selectedChurch?.id
  });

  const [members, setMembers] = useState([]);
  const [originalMembersList, setOriginalMembersList] = useState([]);

  const [teams, setTeams] = useState([]);
  const [originalTeamsList, setOriginalTeamsList] = useState([]);

  const [partners, setPartners] = useState([]);
  const [originalPartnersList, setOriginalPartnersList] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [originalEmployeesList, setOriginalEmployeesList] = useState([]);

  useEffect(() => {
    if (membersQuery.data) {
      setMembers(membersQuery.data.result);
      setOriginalMembersList(membersQuery.data.result);
    }
  }, [membersQuery.data]);

  useEffect(() => {
    if (teamsQuery.data) {
      setTeams(teamsQuery.data.result);
      setOriginalTeamsList(teamsQuery.data.result);
    }
  }, [teamsQuery.data]);

  useEffect(() => {
    if (partnersQuery.data) {
      setPartners(partnersQuery.data.result);
      setOriginalPartnersList(partnersQuery.data.result);
    }
  }, [partnersQuery.data]);

  useEffect(() => {
    if (employeesQuery.data) {
      setEmployees(employeesQuery.data.result);
      setOriginalEmployeesList(employeesQuery.data.result);
    }
  }, [employeesQuery.data]);

  const search = type => {
    if (type === "members") {
      const fuse = new Fuse(originalMembersList, {
        threshold: 0.3,
        keys: ["first_name"]
      });

      if (isEmpty(searchTerm)) {
        setMembers(originalMembersList);
        return;
      }

      const searchResults = fuse.search(searchTerm);

      if (isEmpty(searchResults)) {
        setMembers([]);
      } else {
        setMembers([
          ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
        ]);
      }
    }

    if (type === "teams") {
      const fuse = new Fuse(originalTeamsList, {
        threshold: 0.3,
        keys: ["name"]
      });

      if (isEmpty(searchTerm)) {
        setTeams(originalTeamsList);
        return;
      }

      const searchResults = fuse.search(searchTerm);

      if (isEmpty(searchResults)) {
        setTeams([]);
      } else {
        setTeams([
          ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
        ]);
      }
    }

    if (type === "partners") {
      const fuse = new Fuse(originalPartnersList, {
        threshold: 0.3,
        keys: ["first_name"]
      });

      if (isEmpty(searchTerm)) {
        setPartners(originalPartnersList);
        return;
      }

      const searchResults = fuse.search(searchTerm);

      if (isEmpty(searchResults)) {
        setPartners([]);
      } else {
        setPartners([
          ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
        ]);
      }
    }

    if (type === "employees") {
      const fuse = new Fuse(originalEmployeesList, {
        threshold: 0.3,
        keys: ["first_name"]
      });

      if (isEmpty(searchTerm)) {
        setEmployees(originalEmployeesList);
        return;
      }

      const searchResults = fuse.search(searchTerm);

      if (isEmpty(searchResults)) {
        setEmployees([]);
      } else {
        setEmployees([
          ...map(fuse.search(searchTerm), foundTerm => foundTerm.item)
        ]);
      }
    }
  };

  return (
    <Container>
      <Tabs>
        {tabs.map(tabItem => (
          <Tab
            key={tabItem.name}
            active={tabItem.value === activeTab}
            onClick={() => setActiveTab(tabItem.value)}
          >
            {t(tabItem.name)}
          </Tab>
        ))}
      </Tabs>
      <TextField
        neutral={1}
        placeholder={t(`${translationMapping[activeTab]}`)}
        register={() => {}}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
        onChange={e => setSearchTerm(e.target.value)}
        onKeyUp={() => search(activeTab)}
      />
      {isEmpty(recipients) && (
        <Alert iconBg="#3186EA" bg="#D8E6F9" borderColor="#B4CDEF">
          <span className="startIcon">
            <BsInfoCircle />
          </span>
          {t("message.newMessage.FirstSelect")}
          <span className="endIcon">
            <BsX />
          </span>
        </Alert>
      )}
      <Contacts>
        {activeTab === "members" && (
          <>
            {membersQuery.isLoading ? (
              <LoadingContainer>
                <Loading size={20} />
              </LoadingContainer>
            ) : (
              <>
                {members.length > 0 && (
                  <Contact
                    key="all-members"
                    style={{
                      borderBottom: "1px solid #eee",
                      paddingBottom: "16px"
                    }}
                  >
                    <Checkbox
                      name="all-members"
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: "all-members",
                          checked:
                            members.length > 0 &&
                            members.every(member =>
                              recipients.has(
                                `${member.id}_${member.first_name}_${member.last_name}`
                              )
                            ),
                          indeterminate:
                            members.some(member =>
                              recipients.has(
                                `${member.id}_${member.first_name}_${member.last_name}`
                              )
                            ) &&
                            !members.every(member =>
                              recipients.has(
                                `${member.id}_${member.first_name}_${member.last_name}`
                              )
                            ),
                          onChange: e => {
                            if (e.target.checked) {
                              // Select all members
                              members.forEach(member => {
                                addRecipient(
                                  `${member.id}_${member.first_name}_${member.last_name}`,
                                  member
                                );
                                addMember(member.id, { id: member.id });
                              });
                            } else {
                              // Deselect all members
                              members.forEach(member => {
                                removeRecipient(
                                  `${member.id}_${member.first_name}_${member.last_name}`
                                );
                                removeMember(member.id);
                              });
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <ContactName>
                      {t("message.newMessage.AllMembers")}
                    </ContactName>
                  </Contact>
                )}
                {members.map(member => (
                  <Contact key={member.id}>
                    <Checkbox
                      name={member.id}
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: member.id,
                          checked: recipients.has(
                            `${member.id}_${member.first_name}_${member.last_name}`
                          ),
                          onChange: e => {
                            if (e.target.checked) {
                              addRecipient(
                                `${member.id}_${member.first_name}_${member.last_name}`,
                                member
                              );
                              addMember(member.id, { id: member.id });
                            } else {
                              removeRecipient(
                                `${member.id}_${member.first_name}_${member.last_name}`
                              );
                              removeMember(member.id);
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <Avatar
                      alt={member.first_name}
                      src={`${IMAGE_URL}/${member.image_url}`}
                      sx={{ width: 48, height: 48 }}
                    />
                    <ContactName>
                      {member.first_name} {member.father_name}
                    </ContactName>
                  </Contact>
                ))}
              </>
            )}
          </>
        )}

        {activeTab === "teams" && (
          <>
            {teamsQuery.isLoading ? (
              <LoadingContainer>
                <Loading size={20} />
              </LoadingContainer>
            ) : (
              <>
                {teams.filter(team => team.members_count > 0).length > 0 && (
                  <Contact
                    key="all-teams"
                    style={{
                      borderBottom: "1px solid #eee",
                      paddingBottom: "16px"
                    }}
                  >
                    <Checkbox
                      name="all-teams"
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: "all-teams",
                          checked:
                            teams.filter(team => team.members_count > 0)
                              .length > 0 &&
                            teams
                              .filter(team => team.members_count > 0)
                              .every(team =>
                                recipients.has(`${team.id}_${team.name}`)
                              ),
                          indeterminate:
                            teams
                              .filter(team => team.members_count > 0)
                              .some(team =>
                                recipients.has(`${team.id}_${team.name}`)
                              ) &&
                            !teams
                              .filter(team => team.members_count > 0)
                              .every(team =>
                                recipients.has(`${team.id}_${team.name}`)
                              ),
                          onChange: e => {
                            if (e.target.checked) {
                              // Select all teams
                              teams.forEach(team => {
                                if (team.members_count > 0) {
                                  addRecipient(`${team.id}_${team.name}`, team);
                                  addTeam(team.id, { id: team.id });
                                }
                              });
                            } else {
                              // Deselect all teams
                              teams.forEach(team => {
                                if (team.members_count > 0) {
                                  removeRecipient(`${team.id}_${team.name}`);
                                  removeTeam(team.id);
                                }
                              });
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <ContactName>
                      {t("message.newMessage.AllTeams")}
                    </ContactName>
                  </Contact>
                )}
                {teams.map(team => {
                  if (team.members_count === 0) return null;
                  return (
                    <Contact key={team.id}>
                      <Checkbox
                        name={team.id}
                        buttons={[
                          {
                            checkboxLabel: "",
                            value: true,
                            id: team.id,
                            checked: recipients.has(`${team.id}_${team.name}`),
                            onChange: e => {
                              if (e.target.checked) {
                                addRecipient(`${team.id}_${team.name}`, team);
                                addTeam(team.id, { id: team.id });
                              } else {
                                removeRecipient(`${team.id}_${team.name}`);
                                removeTeam(team.id);
                              }
                            }
                          }
                        ]}
                        error=""
                        helperText=""
                        register={() => {}}
                      />
                      <Avatar
                        alt={team.name}
                        src={`${IMAGE_URL}/${team.image_url}`}
                        sx={{ width: 48, height: 48 }}
                      />
                      <ContactName>{team.name}</ContactName>
                    </Contact>
                  );
                })}
              </>
            )}
          </>
        )}

        {activeTab === "partners" && (
          <>
            {partnersQuery.isLoading ? (
              <LoadingContainer>
                <Loading size={20} />
              </LoadingContainer>
            ) : (
              <>
                {partners.length > 0 && (
                  <Contact
                    key="all-partners"
                    style={{
                      borderBottom: "1px solid #eee",
                      paddingBottom: "16px"
                    }}
                  >
                    <Checkbox
                      name="all-partners"
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: "all-partners",
                          checked:
                            partners.length > 0 &&
                            partners.every(partner =>
                              recipients.has(
                                `${partner.id}_${partner.first_name}_${partner.last_name}`
                              )
                            ),
                          indeterminate:
                            partners.some(partner =>
                              recipients.has(
                                `${partner.id}_${partner.first_name}_${partner.last_name}`
                              )
                            ) &&
                            !partners.every(partner =>
                              recipients.has(
                                `${partner.id}_${partner.first_name}_${partner.last_name}`
                              )
                            ),
                          onChange: e => {
                            if (e.target.checked) {
                              // Select all partners
                              partners.forEach(partner => {
                                addRecipient(
                                  `${partner.id}_${partner.first_name}_${partner.last_name}`,
                                  partner
                                );
                                addPartner(partner.id, { id: partner.id });
                              });
                            } else {
                              // Deselect all partners
                              partners.forEach(partner => {
                                removeRecipient(
                                  `${partner.id}_${partner.first_name}_${partner.last_name}`
                                );
                                removePartner(partner.id);
                              });
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <ContactName>
                      {t("message.newMessage.AllPartners")}
                    </ContactName>
                  </Contact>
                )}
                {partners.map(partner => (
                  <Contact key={partner.id}>
                    <Checkbox
                      name={partner.id}
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: partner.id,
                          checked: recipients.has(
                            `${partner.id}_${partner.first_name}_${partner.last_name}`
                          ),
                          onChange: e => {
                            if (e.target.checked) {
                              addRecipient(
                                `${partner.id}_${partner.first_name}_${partner.last_name}`,
                                partner
                              );
                              addPartner(partner.id, { id: partner.id });
                            } else {
                              removeRecipient(
                                `${partner.id}_${partner.first_name}_${partner.last_name}`
                              );
                              removePartner(partner.id);
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <Avatar
                      alt={partner.first_name}
                      src={`${IMAGE_URL}/${partner.image_url}`}
                      sx={{ width: 48, height: 48 }}
                    />
                    {partner.partner_type === "individual" && (
                      <ContactName>
                        {partner.first_name} {partner.last_name}
                      </ContactName>
                    )}
                    {partner.partner_type === "organization" && (
                      <ContactName>
                        {partner.first_name} {partner.last_name} |{" "}
                        {partner.organization_name}
                      </ContactName>
                    )}
                  </Contact>
                ))}
              </>
            )}
          </>
        )}

        {activeTab === "employees" && (
          <>
            {employeesQuery.isLoading ? (
              <LoadingContainer>
                <Loading size={20} />
              </LoadingContainer>
            ) : (
              <>
                {employees.length > 0 && (
                  <Contact
                    key="all-employees"
                    style={{
                      borderBottom: "1px solid #eee",
                      paddingBottom: "16px"
                    }}
                  >
                    <Checkbox
                      name="all-employees"
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: "all-employees",
                          checked:
                            employees.length > 0 &&
                            employees.every(employee =>
                              recipients.has(
                                `${employee.id}_${employee.first_name}_${employee.last_name}`
                              )
                            ),
                          indeterminate:
                            employees.some(employee =>
                              recipients.has(
                                `${employee.id}_${employee.first_name}_${employee.last_name}`
                              )
                            ) &&
                            !employees.every(employee =>
                              recipients.has(
                                `${employee.id}_${employee.first_name}_${employee.last_name}`
                              )
                            ),
                          onChange: e => {
                            if (e.target.checked) {
                              // Select all employees
                              employees.forEach(employee => {
                                addRecipient(
                                  `${employee.id}_${employee.first_name}_${employee.last_name}`,
                                  employee
                                );
                                addEmployee(employee.id, { id: employee.id });
                              });
                            } else {
                              // Deselect all employees
                              employees.forEach(employee => {
                                removeRecipient(
                                  `${employee.id}_${employee.first_name}_${employee.last_name}`
                                );
                                removeEmployee(employee.id);
                              });
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <ContactName>
                      {t("message.newMessage.AllEmployees")}
                    </ContactName>
                  </Contact>
                )}
                {employees.map(employee => (
                  <Contact key={employee.id}>
                    <Checkbox
                      name={employee.id}
                      buttons={[
                        {
                          checkboxLabel: "",
                          value: true,
                          id: employee.id,
                          checked: recipients.has(
                            `${employee.id}_${employee.first_name}_${employee.last_name}`
                          ),
                          onChange: e => {
                            if (e.target.checked) {
                              addRecipient(
                                `${employee.id}_${employee.first_name}_${employee.last_name}`,
                                employee
                              );
                              addEmployee(employee.id, { id: employee.id });
                            } else {
                              removeRecipient(
                                `${employee.id}_${employee.first_name}_${employee.last_name}`
                              );
                              removeEmployee(employee.id);
                            }
                          }
                        }
                      ]}
                      error=""
                      helperText=""
                      register={() => {}}
                    />
                    <Avatar
                      alt={employee.first_name}
                      src={`${IMAGE_URL}/${employee?.employee_photo}`}
                      sx={{ width: 48, height: 48 }}
                    />
                    <ContactName>
                      {employee.first_name} {employee.middle_name}
                    </ContactName>
                  </Contact>
                ))}
              </>
            )}
          </>
        )}
      </Contacts>
    </Container>
  );
};
